import { extractImageMetadata } from "@/shared/lib/image-metadata"
import { extractVideoMetadata } from "@/shared/lib/video-metadata"

/** Как файл уйдёт в пост. Совпадает с типами медиа в API. */
export type StagedKind = "photo" | "video" | "document" | "voice"

/** Файл, выбранный в редакторе, но ещё не загруженный. */
export interface StagedMedia {
	key: string
	file: File
	kind: StagedKind
	/** Адрес для показа в браузере до загрузки. */
	objectUrl: string
	width?: number
	height?: number
	duration?: number
	poster?: StagedPoster
}

export interface StagedPoster {
	file: File
	objectUrl: string
	width?: number
	height?: number
}

export const ACCEPTED_MEDIA = "image/*,video/*,audio/*,.pdf,.doc,.docx,.zip"

export function kindOf(file: File): StagedKind {
	const mime = file.type || ""
	if (mime.startsWith("image/")) return "photo"
	if (mime.startsWith("video/")) return "video"
	if (mime.startsWith("audio/")) return "voice"
	return "document"
}

let counter = 0

/** Готовит файл к показу: адрес, размеры и длительность, если их можно прочитать. */
export async function stageFile(file: File): Promise<StagedMedia> {
	const kind = kindOf(file)
	const base: StagedMedia = {
		key: `staged-${Date.now()}-${counter++}`,
		file,
		kind,
		objectUrl: URL.createObjectURL(file),
	}
	try {
		if (kind === "video") return { ...base, ...(await extractVideoMetadata(file)) }
		if (kind === "photo") return { ...base, ...(await extractImageMetadata(file)) }
	} catch {
		// Размеры не прочитались: файл всё равно загрузится, Telegram посчитает сам.
	}
	return base
}

export async function stagePoster(file: File): Promise<StagedPoster> {
	const poster: StagedPoster = { file, objectUrl: URL.createObjectURL(file) }
	try {
		return { ...poster, ...(await extractImageMetadata(file)) }
	} catch {
		return poster
	}
}

/**
 * Кадр видео как картинка. Работает для локального файла всегда, а для загруженного,
 * только если хранилище отдаёт видео с CORS: иначе браузер не даст прочитать пиксели.
 */
export async function captureFrame(video: HTMLVideoElement, name = "poster.jpg"): Promise<File> {
	const canvas = document.createElement("canvas")
	canvas.width = video.videoWidth
	canvas.height = video.videoHeight
	const context = canvas.getContext("2d")
	if (!context || !canvas.width) throw new Error("Видео ещё не загрузилось")
	context.drawImage(video, 0, 0, canvas.width, canvas.height)
	const blob = await new Promise<Blob | null>((resolve) => {
		try {
			canvas.toBlob(resolve, "image/jpeg", 0.9)
		} catch {
			resolve(null)
		}
	})
	if (!blob) throw new Error("Браузер не дал взять кадр из этого видео. Загрузите картинку файлом.")
	return new File([blob], name, { type: "image/jpeg" })
}

export function releaseStaged(item: StagedMedia): void {
	URL.revokeObjectURL(item.objectUrl)
	if (item.poster) URL.revokeObjectURL(item.poster.objectUrl)
}
